/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import * as loginActions from "@/src/store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "@/src/services/landingPagesList";
import Typography from "@mui/material/Typography";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import TenancyLawCheckinForm from "./TenancyLawCheckinForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import tenancyLawCheckinStyle from "./tenancyLawCheckinStyle";
import {Box} from "@mui/material";
import { getPartnerNameByIri, PARTNER_IRIS } from "@/src/constants/partnerIris";
import type { PartnerIri } from "@/src/constants/partnerIris";

type TenancyLawCheckinProps = {
  idPrefix: string;
  color?: "default" | "grey";
  target?: string;
  checkinHeading?: string;
  partner?: PartnerIri;
};

const TenancyLawCheckin = ({
  color = "default",
  target = "/servicewelt",
  idPrefix,
  checkinHeading = "Fall schildern und professionelle Hilfe erhalten",
  partner = PARTNER_IRIS.ADVOCARD,
}: TenancyLawCheckinProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const initialValues = {
    reasonForDispute: "",
    email: "",
  };

  const handleSubmit = async ({ values }: { values: Record<string, any> }) => {
    setIsLoading(true);

    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email);
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const partnerName = getPartnerNameByIri(partner);
    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      prelead: {
        initialProduct: "rentalContract",
        landingPage: landingPage.keyword,
        partner: partnerName,
      },
    };

    await loginActions.register(data);
    await createMutation.mutateAsync({
      uri: "/rental_contracts",
      data: {
        reasonForDispute: values.reasonForDispute,
        acquisitionPartner: partner,
        affiliateCode: localStorage.getItem("gclid"),
        landingPage: landingPage.keyword,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={[tenancyLawCheckinStyle.formPaper, tenancyLawCheckinStyle[color]]}>
      <Box id={idPrefix + "-checkin"} />
      <Typography variant={"h5"} sx={tenancyLawCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <TenancyLawCheckinForm isLoading={isLoading} emailErrorText={emailErrorText} setEmailErrorText={setEmailErrorText} />
      </FormProvider>
    </Paper>
  );
};

export default TenancyLawCheckin;
